// src/pages/admin/AdminStats.js
// Statistiques détaillées connectées à l'API - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import apiClient from '../../api/client';

const AdminStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const result = await apiClient.get('/endpoints/stats.php?action=dashboard');
        setStats(result);
      } catch (err) {
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const categoryLabels = {
    locations: 'Locations',
    sejours: 'Séjours',
    immobilier: 'Immobilier',
    services: 'Services',
  };
  const categoryColors = {
    locations: '#48BB78',
    sejours: '#4299E1',
    immobilier: '#FFD700',
    services: '#ED8936',
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', padding: '20px' },
    statCard: { padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', flexDirection: 'column', gap: '6px' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#FFD700' },
    statLabel: { fontSize: '12px', color: '#A0AEC0' },
    sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#FFFFFF', padding: '8px 20px 12px' },
    chartCard: { margin: '0 20px 20px', padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    barRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
    barLabel: { fontSize: '13px', color: '#A0AEC0', width: '90px', flexShrink: 0 },
    barTrack: { flex: 1, height: '10px', backgroundColor: '#111827', borderRadius: '5px', overflow: 'hidden' },
    barFill: { height: '100%', borderRadius: '5px' },
    barValue: { fontSize: '13px', fontWeight: '600', color: '#FFFFFF', width: '30px', textAlign: 'right' },
    empty: { textAlign: 'center', padding: '48px', color: '#A0AEC0', fontSize: '14px' },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
          <div style={styles.title}>Statistiques</div>
        </div>
        <div style={styles.empty}>Chargement...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
          <div style={styles.title}>Statistiques</div>
        </div>
        <div style={styles.empty}>Impossible de charger les statistiques</div>
      </div>
    );
  }

  const byCategoryListings = stats.byCategoryListings || [];
  const byCityListings = stats.byCityListings || [];
  const maxCategoryCount = byCategoryListings.length > 0 ? Math.max(...byCategoryListings.map((c) => Number(c.count))) : 1;
  const maxCityCount = byCityListings.length > 0 ? Math.max(...byCityListings.map((c) => Number(c.count))) : 1;

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Statistiques | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Statistiques</div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalListings}</div>
          <div style={styles.statLabel}>Annonces totales</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#48BB78' }}>{stats.publishedListings}</div>
          <div style={styles.statLabel}>Publiées</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#ED8936' }}>{stats.pendingListings}</div>
          <div style={styles.statLabel}>En attente</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#4299E1' }}>{stats.totalReservations}</div>
          <div style={styles.statLabel}>Demandes de contact</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalUsers}</div>
          <div style={styles.statLabel}>Utilisateurs</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#48BB78' }}>+{stats.newUsersThisMonth}</div>
          <div style={styles.statLabel}>Nouveaux ce mois</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Annonces par catégorie</div>
      <div style={styles.chartCard}>
        {byCategoryListings.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#A0AEC0' }}>Aucune donnée pour le moment</div>
        ) : (
          byCategoryListings.map((item) => (
            <div key={item.category} style={styles.barRow}>
              <span style={styles.barLabel}>{categoryLabels[item.category] || item.category}</span>
              <div style={styles.barTrack}>
                <div
                  style={{
                    ...styles.barFill,
                    width: `${(Number(item.count) / maxCategoryCount) * 100}%`,
                    backgroundColor: categoryColors[item.category] || '#FFD700',
                  }}
                />
              </div>
              <span style={styles.barValue}>{item.count}</span>
            </div>
          ))
        )}
      </div>

      <div style={styles.sectionTitle}>Annonces par ville</div>
      <div style={styles.chartCard}>
        {byCityListings.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#A0AEC0' }}>Aucune donnée pour le moment</div>
        ) : (
          byCityListings.map((item) => (
            <div key={item.city} style={styles.barRow}>
              <span style={styles.barLabel}>{item.city}</span>
              <div style={styles.barTrack}>
                <div
                  style={{
                    ...styles.barFill,
                    width: `${(Number(item.count) / maxCityCount) * 100}%`,
                    backgroundColor: '#FFD700',
                  }}
                />
              </div>
              <span style={styles.barValue}>{item.count}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminStats;